<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col cols="4" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Strona główna</span>
        <h3 class="page-title">Witaj w panelu</h3>
      </d-col>

      <d-col cols="8" class="text-right">
        <d-button
          theme="primary"
          class="mt-3"
          @click.prevent="loadData"
          :disabled="!loaded">
          <template v-if="loaded">
            <i class="material-icons">refresh</i>
            Odśwież
          </template>
          <template v-if="!loaded">
            <div
              class="spinner-border spinner-border-sm"
              role="status"></div>
          </template>
        </d-button>

        <d-button
          theme="danger"
          class="mt-3 ml-3"
          @click.prevent="$router.push('/wyloguj')">
          <i class="material-icons">logout</i>
          Wyloguj
        </d-button>
      </d-col>
    </d-row>

    <d-row v-if="!loaded">
      <d-alert
        show
        theme="light"
        class="text-center w-100">
        Trwa wczytywanie danych...
      </d-alert>
    </d-row>

    <template v-if="loaded">
      <d-row
        v-if="!hasPatternItems && !hasSewingItems">
        <d-alert
          show
          theme="warning"
          class="text-center">
          Brak zleceń do wykonania...
        </d-alert>
      </d-row>

      <d-row
        v-if="hasPatternItems"
        key="order-group-pattern"
        class="my-4 px-3">
        <h4 class="pt-3 mb-4 mt-3">
          Wykrajanie
          <d-badge
            class="ml-2"
            style="position: relative; top: -2px;">
            {{ patternItemsCount }}
          </d-badge>
        </h4>

        <d-card class="w-100">
          <table class="table mt-0 mb-0 full-width items-list">
            <thead class="bg-light">
              <tr>
                <th scope="col" class="border-0">Nazwa zamówienia</th>
                <th scope="col" class="border-0" style="width: 100px">Otrzymano</th>
                <th scope="col" class="border-0" style="width: 100px">Termin</th>
                <th scope="col" class="border-0" style="width: 300px;">Produkt</th>
                <th scope="col" class="border-0" style="width: 300px;">Informacje dodatkowe</th>
                <th scope="col" class="border-0">Realizacja</th>
                <th scope="col" class="border-0" style="width: 140px;">Wykonanie</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) of patternItems"
                :key="'pattern-item-' + index">
                <td class="align-middle">
                  <small>
                    <strong>{{ item.order_name }}</strong>
                    <br>
                    ID zamówienia: #{{ item.order_id }}
                  </small>
                </td>

                <td class="align-middle">
                  <small>
                    {{ item.created_at }}
                  </small>
                </td>

                <td class="align-middle">
                  <small :class="dateColor(item.end_at)">
                    {{ item.end_at }}
                  </small>
                </td>

                <td class="align-middle">
                  <dl>
                    <dt>
                      Kod produktu:
                    </dt>
                    <dd>
                      {{ item.product_code }}
                    </dd>

                    <template v-if="item.color_name">
                      <dt>
                        Kolor produktu:
                      </dt>
                      <dd>
                        {{ item.color_name }}
                      </dd>
                    </template>

                    <template v-if="item.polar_name">
                      <dt>
                        Kolor polaru:
                      </dt>
                      <dd>
                        {{ item.polar_name }}
                      </dd>
                    </template>
                  </dl>
                </td>

                <td class="align-middle">
                  <dl>
                    <dt>
                      Wykrój:
                    </dt>
                    <dd>
                      <span
                        v-if="item.pattern_before_order"
                        class="text-success">
                        Tak
                      </span>
                      <span
                        v-else
                        class="text-danger">
                        Nie
                      </span>
                    </dd>

                    <dt>
                      Zostaje w Hafciarni:
                    </dt>
                    <dd>
                      <span
                        v-if="item.pattern_stays"
                        class="text-success">
                        Tak
                      </span>
                      <span
                        v-else
                        class="text-danger">
                        Nie
                      </span>
                    </dd>

                    <template v-if="item.seam_name">
                      <dt>
                        Przeszycia:
                      </dt>
                      <dd>
                        {{ item.seam_name }}
                      </dd>
                    </template>

                    <template v-if="item.size_name">
                      <dt>
                        Rozmiar:
                      </dt>
                      <dd>
                        {{ item.size_name }}
                      </dd>
                    </template>

                    <template v-if="item.material_name">
                      <dt>
                        Materiał:
                      </dt>
                      <dd>
                        {{ item.material_name }}
                      </dd>
                    </template>

                    <template v-if="item.description">
                      <dt>
                        Uwagi:
                      </dt>
                      <dd>
                        {{ item.description }}
                      </dd>
                    </template>
                  </dl>
                </td>

                <td class="align-middle">
                  <span
                    :class="{
                      'text-success': item.quantity_done === item.quantity,
                      'text-danger': item.quantity_done < item.quantity && item.quantity_done > 0
                    }">
                    {{ item.quantity_done }} z {{ item.quantity }} szt.
                  </span>
                </td>

                <td class="align-middle">
                  <d-button
                    theme="success"
                    class="mt-2 w-100"
                    size="sm"
                    @click.prevent="openPopup(item)">
                    Aktualizuj
                  </d-button>
                </td>
              </tr>
            </tbody>
          </table>
        </d-card>
      </d-row>

      <d-row
        v-if="hasSewingItems"
        key="order-group-sewing"
        class="my-4 px-3">
        <h4 class="pt-3 mb-4 mt-3">
          Szycie
          <d-badge
            class="ml-2"
            style="position: relative; top: -2px;">
            {{ sewingItemsCount }}
          </d-badge>
        </h4>

        <d-card class="w-100">
          <validation-observer
            ref="sewing-forms"
            tag="div">
            <table class="table mt-0 mb-0 full-width items-list">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0">Nazwa procesu</th>
                  <th scope="col" class="border-0">Nazwa zamówienia</th>
                  <th scope="col" class="border-0" style="width: 100px">Termin</th>
                  <th scope="col" class="border-0" style="width: 300px;">Produkt</th>
                  <th scope="col" class="border-0" style="width: 300px;">Informacje dodatkowe</th>
                  <th scope="col" class="border-0">Realizacja</th>
                  <th scope="col" class="border-0" style="width: 140px;">Wykonanie</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) of sewingItems"
                  :key="'sewing-item-' + index">
                  <td class="align-middle">
                    <small>
                      <strong>{{ item.process_name }}</strong>
                    </small>
                  </td>

                  <td class="align-middle">
                    <small>
                      <strong>{{ item.order_name }}</strong>
                      <br>
                      ID zamówienia: #{{ item.order_id }}
                    </small>
                  </td>

                  <td class="align-middle">
                    <small :class="dateColor(item.end_at)">
                      {{ item.end_at }}
                    </small>
                  </td>

                  <td class="align-middle">
                    <dl>
                      <dt>
                        Kod produktu:
                      </dt>
                      <dd>
                        {{ item.product_code }}
                      </dd>
                    </dl>
                  </td>

                  <td class="align-middle">
                    <dl>
                      <template v-if="item.strand_name">
                        <dt>
                          Kolor nici:
                        </dt>
                        <dd>
                          {{ item.strand_name }}
                        </dd>
                      </template>

                      <template v-if="item.material_name">
                        <dt>
                          Materiał:
                        </dt>
                        <dd>
                          {{ item.material_name }}
                        </dd>
                      </template>

                      <template v-if="item.color_name">
                        <dt>
                          Kolor:
                        </dt>
                        <dd>
                          {{ item.color_name }}
                        </dd>
                      </template>

                      <template v-if="item.seam_name">
                        <dt>
                          Przeszycia:
                        </dt>
                        <dd>
                          {{ item.seam_name }}
                        </dd>
                      </template>

                      <template v-if="item.description">
                        <dt>
                          Uwagi:
                        </dt>
                        <dd>
                          {{ item.description }}
                        </dd>
                      </template>
                    </dl>
                  </td>

                  <td class="align-middle">
                    <span
                      :class="{
                        'text-success': item.quantity_done === item.quantity,
                        'text-danger': item.quantity_done < item.quantity && item.quantity_done > 0
                      }">
                      {{ item.quantity_done }} z {{ item.quantity }} szt.
                    </span>
                  </td>

                  <td class="align-middle">
                    <d-button
                      theme="success"
                      class="mt-2 w-100"
                      size="sm"
                      @click.prevent="openPopup(item)">
                      Aktualizuj
                    </d-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </validation-observer>
        </d-card>
      </d-row>
    </template>

    <d-modal
      v-if="updatePopupVisible"
      @close="closePopup">
      <d-modal-header>
        <d-modal-title>
          Aktualizuj stan realizacji
        </d-modal-title>
      </d-modal-header>
      <d-modal-body class="p-5">
        <p>
          Zrealizowano dotąd: {{ popupState.quantity_done }} z {{ popupState.quantity }} szt.
        </p>

        <validation-provider
          :rules="'required|min_value:1|max_value:' + (popupState.quantity - popupState.quantity_done)"
          name="items-done"
          v-slot="{ errors }">
          <div class="w-100">
            <d-input-group
              append="szt."
              prepend="Oznacz jako zrealizowane:"
              :state="errors.length ? 'invalid' : null">
              <d-input
                type="number"
                :min="1"
                :step="1"
                :max="popupState.quantity - popupState.quantity_done"
                :state="errors.length ? 'invalid' : null"
                class="text-right"
                v-model="popupState.done" />
            </d-input-group>
            <div class="invalid-feedback text-center">
              {{ errors[0] }}
            </div>
          </div>
          <d-button
            theme="success"
            class="mt-2 w-100"
            size="sm"
            @click.prevent="savePopup"
            :disabled="popupState.saveInProgress || !!errors.length">
            <div
              v-if="popupState.saveInProgress"
              class="spinner-border spinner-border-sm"
              role="status"></div>
            Aktualizuj
          </d-button>
        </validation-provider>
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';

export default {
  name: 'dashboard',
  computed: {
    hasSewingItems () {
      return !!this.items.filter(item => item.process_type === 'SEWING').length;
    },
    hasPatternItems () {
      return !!this.items.filter(item => item.process_type === 'PATTERN').length;
    },
    sewingItems () {
      return this.items.filter(item => item.process_type === 'SEWING');
    },
    patternItems () {
      return this.items.filter(item => item.process_type === 'PATTERN');
    },
    sewingItemsCount () {
      return this.items.filter(item => item.process_type === 'SEWING').length;
    },
    patternItemsCount () {
      return this.items.filter(item => item.process_type === 'PATTERN').length;
    }
  },
  data() {
    return {
      updatePopupVisible: false,
      items: [],
      sewingDone: [],
      patternDone: [],
      loaded: false,
      popupState: {
        itemID: 0,
        quantity: 0,
        quantity_done: 0,
        done: 1,
        saveInProgress: false
      }
    };
  },
  mounted () {
    this.loadData();
  },
  methods: {
    loadData () {
      this.loaded = false;

      ListUtils.loadItemsData(this, {
        endpoint: '/orders/products/processes/items/items',
        listField: ['items'],
        params: {
          pagination: 0
        },
        pagination: false
      });
    },
    dateColor (dateToCheck) {
      let timestamp = +new Date(dateToCheck);
      let currentTimestamp = +new Date();

      if (timestamp - currentTimestamp < 0) {
        return 'text-danger font-weight-bold';
      } else if (timestamp - currentTimestamp < 4 * 24 * 60 * 60 * 1000) {
        return 'text-warning';
      }

      return 'text-primary';
    },
    openPopup (item) {
      this.popupState.itemID = item.id;
      this.popupState.quantity = item.quantity;
      this.popupState.quantity_done = item.quantity_done;
      this.popupState.done = 1;
      this.popupState.saveInProgress = false;
      this.updatePopupVisible = true;
    },
    closePopup () {
      this.popupState.itemID = 0;
      this.popupState.quantity = 0;
      this.popupState.quantity_done = 0;
      this.popupState.done = 1;
      this.popupState.saveInProgress = false;
      this.updatePopupVisible = false;
    },
    savePopup () {
      this.popupState.saveInProgress = true;

      FormUtils.submit(this, {
        endpoint: '/orders/products/processes/items/store-execution/' + parseInt(this.popupState.itemID, 10),
        formData: {
          execution: parseInt(this.popupState.done, 10)
        },
        successTitle: 'Zapisano stan',
        successText: 'Stan realizacji wybranej pozycji został zaktualizowany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja stanu nie powiodła się. Spróbuj ponownie.',
        beforeSuccessAction: () => {
          this.closePopup();
        },
        beforeFailAction: () => {
          this.closePopup();
        },
        successAction: () => {
          this.loadData();
        }
      });
    }
  }
};
</script>

<style lang="scss">
.items-list dl {
  display: flex;
  flex-wrap: wrap;
  font-size: 80%!important;
  margin: 0;
  width: 300px;

  dt {
    width: 140px;
  }
  dd {
    margin: 0;
    width: calc(100% - 140px);
  }
}

.input-group[state="invalid"] + .invalid-feedback {
  display: block;
}
</style>
